import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, Infinity as InfinityIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

/** Compact AI-credit balance pill in the header. Click → /billing. */
const CreditBalanceWidget = () => {
  const { getAuthHeaders, token } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.revAIWallet(getAuthHeaders());
      setWallet(res.data);
    } catch (e) { /* non-critical */ }
  }, [token]);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener('credits:refresh', onRefresh);
    const iv = setInterval(load, 60000);
    return () => { window.removeEventListener('credits:refresh', onRefresh); clearInterval(iv); };
  }, [load]);

  if (!wallet) return null;
  const exempt = wallet.exempt;
  const available = wallet.available_credits ?? 0;
  const low = !exempt && available <= (wallet.purchased_credits || 1000) * 0.2;

  return (
    <button
      onClick={() => navigate('/billing')}
      data-testid="credit-balance-widget"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
        low ? 'bg-amber-50 border-amber-300 hover:bg-amber-100'
            : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
      }`}
      title="AI Credits — click to manage"
    >
      <Coins className={`w-4 h-4 ${low ? 'text-amber-600' : 'text-amber-500'}`} />
      {exempt ? (
        <span className="flex items-center gap-1 text-sm font-semibold text-emerald-700">
          <InfinityIcon className="w-4 h-4" /> Credits
        </span>
      ) : (
        <span className={`text-sm font-semibold ${low ? 'text-amber-700' : 'text-slate-700'}`}>
          {available.toLocaleString()} <span className="font-normal text-slate-400">credits</span>
        </span>
      )}
    </button>
  );
};

export default CreditBalanceWidget;
